import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { TrendingUp, Eye, Inbox, Zap, ExternalLink, Trophy, AlertTriangle, BarChart3 } from 'lucide-react';
import { API, adminAuthHeader } from '../api';
import AdminSearch from './AdminSearch';

const StatCard = ({ label, value, icon: Icon, accent = 'text-gray-900' }) => (
  <div className="bg-white border border-gray-200 rounded-lg px-4 py-3.5" data-testid={`analytics-total-${label.toLowerCase().replace(/\s/g, '-')}`}>
    <div className="flex items-center justify-between">
      <span className="text-[11px] uppercase tracking-widest text-gray-500">{label}</span>
      <Icon size={14} className="text-gray-400" />
    </div>
    <div className={`text-2xl font-bold mt-1 ${accent}`}>{value}</div>
  </div>
);

const ConvBar = ({ pct }) => {
  const width = Math.min(Math.max(pct, 0), 100);
  const tone = pct >= 8 ? 'bg-green-500' : pct >= 3 ? 'bg-[#ffcc29]' : pct > 0 ? 'bg-orange-400' : 'bg-gray-200';
  return (
    <div className="w-full h-1.5 bg-gray-100 rounded-full overflow-hidden">
      <div className={`h-full ${tone} rounded-full transition-all`} style={{ width: `${width}%` }} />
    </div>
  );
};

const CampaignAnalytics = () => {
  const [data, setData] = useState(null);
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => { load(); }, []);
  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/landing-pages/analytics`, { headers: adminAuthHeader() });
      setData(res.data);
    } catch (_) { /* ignore */ } finally { setLoading(false); }
  };

  if (loading) return <div className="text-center py-16 text-gray-500 text-sm">Loading analytics...</div>;
  if (!data || !data.rows) return <div className="text-center py-16 text-gray-500 text-sm">No data available.</div>;

  const totals = data.totals || {};
  const rows = data.rows || [];
  const withEnquiries = rows.filter((r) => r.enquiries > 0);
  const winner = withEnquiries[0];
  const worst = [...withEnquiries].reverse()[0];

  return (
    <div data-testid="campaign-analytics">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <BarChart3 size={20} className="text-[#0d64a8]" /> Campaign Analytics
        </h2>
        <p className="text-sm text-gray-500 mt-1">Landing pages ranked by conversion. Winners at the top, quiet campaigns at the bottom.</p>
      </div>

      {/* Topline totals */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <StatCard label="Landing Pages" value={totals.landing_pages || 0} icon={BarChart3} />
        <StatCard label="Total Visits" value={(totals.total_visits || 0).toLocaleString('en-IN')} icon={Eye} />
        <StatCard label="Total Enquiries" value={(totals.total_enquiries || 0).toLocaleString('en-IN')} icon={Inbox} accent="text-[#0d64a8]" />
        <StatCard label="Avg Conversion" value={`${totals.avg_conversion_pct || 0}%`} icon={TrendingUp} accent="text-green-600" />
      </div>

      {/* Winner / Underperformer callouts */}
      {(winner || worst) && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-6">
          {winner && (
            <div className="bg-gradient-to-br from-green-50 to-white border border-green-200 rounded-lg p-4" data-testid="analytics-winner">
              <div className="flex items-center gap-2 mb-1">
                <Trophy size={14} className="text-green-600" />
                <span className="text-[11px] uppercase tracking-widest text-green-700 font-semibold">Top Performer</span>
              </div>
              <a href={`/p/${winner.slug}`} target="_blank" rel="noreferrer" className="text-base font-bold text-gray-900 hover:text-[#0d64a8] block">{winner.title}</a>
              <p className="text-xs text-gray-500 font-mono">/p/{winner.slug}</p>
              <p className="text-sm text-gray-700 mt-2">
                <span className="font-bold text-green-600">{winner.conversion_pct}%</span> conversion
                <span className="text-gray-500"> · {winner.enquiries} enq / {winner.visits} visits</span>
              </p>
            </div>
          )}
          {worst && worst.slug !== winner?.slug && (
            <div className="bg-gradient-to-br from-orange-50 to-white border border-orange-200 rounded-lg p-4" data-testid="analytics-worst">
              <div className="flex items-center gap-2 mb-1">
                <AlertTriangle size={14} className="text-orange-600" />
                <span className="text-[11px] uppercase tracking-widest text-orange-700 font-semibold">Needs a Refresh</span>
              </div>
              <a href={`/p/${worst.slug}`} target="_blank" rel="noreferrer" className="text-base font-bold text-gray-900 hover:text-[#0d64a8] block">{worst.title}</a>
              <p className="text-xs text-gray-500 font-mono">/p/{worst.slug}</p>
              <p className="text-sm text-gray-700 mt-2">
                <span className="font-bold text-orange-600">{worst.conversion_pct}%</span> conversion
                <span className="text-gray-500"> · {worst.enquiries} enq / {worst.visits} visits</span>
              </p>
            </div>
          )}
        </div>
      )}

      {/* Ranking table */}
      <div className="mb-3">
        <AdminSearch value={q} onChange={setQ} placeholder="Search campaign title or slug..." testId="analytics-search" />
      </div>
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden" data-testid="analytics-ranking-table">
        <div className="hidden md:grid grid-cols-12 gap-3 px-4 py-3 bg-gray-50 border-b border-gray-200 text-[11px] uppercase tracking-widest text-gray-500 font-semibold">
          <div className="col-span-1 text-center">Rank</div>
          <div className="col-span-4">Landing Page</div>
          <div className="col-span-1 text-right">Visits</div>
          <div className="col-span-1 text-right">Enq.</div>
          <div className="col-span-1 text-right">30d</div>
          <div className="col-span-3">Conversion</div>
          <div className="col-span-1"></div>
        </div>
        {rows.length === 0 ? (
          <div className="text-center py-14 text-sm text-gray-500">No landing pages yet. Create your first one under Content → Landing Pages.</div>
        ) : rows.filter((r) => {
          const s = q.trim().toLowerCase();
          if (!s) return true;
          return [r.title, r.slug, r.subtitle].filter(Boolean).join(' ').toLowerCase().includes(s);
        }).map((r, idx) => (
          <div
            key={r.slug}
            data-testid={`analytics-row-${r.slug}`}
            className="grid grid-cols-2 md:grid-cols-12 gap-3 items-center px-4 py-3.5 border-b last:border-b-0 border-gray-100 hover:bg-gray-50 transition"
          >
            <div className="col-span-1 md:text-center flex md:block items-center gap-2">
              <span className={`inline-flex items-center justify-center w-7 h-7 rounded-full text-xs font-bold ${idx === 0 ? 'bg-[#ffcc29] text-[#0a2a4a]' : idx < 3 ? 'bg-[#e8f2fb] text-[#0d64a8]' : 'bg-gray-100 text-gray-600'}`}>
                {idx + 1}
              </span>
            </div>
            <div className="col-span-2 md:col-span-4 min-w-0">
              <div className="flex items-center gap-2">
                <p className="font-semibold text-sm text-gray-900 truncate">{r.title}</p>
                {r.published === false && (
                  <span className="text-[9px] font-medium uppercase tracking-widest px-1.5 py-0.5 rounded bg-gray-100 text-gray-500 shrink-0">Draft</span>
                )}
              </div>
              <p className="text-[11px] text-gray-500 font-mono">/p/{r.slug}</p>
              {Object.keys(r.utm_breakdown || {}).length > 0 && (
                <div className="flex flex-wrap gap-1 mt-1.5">
                  {Object.entries(r.utm_breakdown).slice(0, 4).map(([medium, count]) => (
                    <span key={medium} className="text-[9.5px] bg-[#fff8e1] text-[#a06400] border border-[#ffcc29]/40 rounded px-1.5 py-0.5">
                      {medium}<span className="opacity-70"> · {count}</span>
                    </span>
                  ))}
                </div>
              )}
            </div>
            <div className="col-span-1 md:text-right text-sm text-gray-700">
              <span className="md:hidden text-[10px] uppercase tracking-widest text-gray-400 mr-1">Visits</span>
              {r.visits.toLocaleString('en-IN')}
            </div>
            <div className="col-span-1 md:text-right text-sm font-semibold text-[#0d64a8]">
              <span className="md:hidden text-[10px] uppercase tracking-widest text-gray-400 mr-1 font-normal">Enq.</span>
              {r.enquiries}
            </div>
            <div className="col-span-1 md:text-right text-xs text-gray-500">
              <span className="md:hidden text-[10px] uppercase tracking-widest text-gray-400 mr-1">30d</span>
              {r.recent_enquiries_30d}
            </div>
            <div className="col-span-2 md:col-span-3">
              <div className="flex items-center gap-2">
                <span className={`text-sm font-bold ${r.conversion_pct >= 8 ? 'text-green-600' : r.conversion_pct >= 3 ? 'text-[#a06400]' : r.conversion_pct > 0 ? 'text-orange-600' : 'text-gray-400'}`}>
                  {r.conversion_pct}%
                </span>
                <div className="flex-1"><ConvBar pct={r.conversion_pct} /></div>
              </div>
            </div>
            <div className="col-span-2 md:col-span-1 md:text-right">
              <a
                href={`/p/${r.slug}`}
                target="_blank"
                rel="noreferrer"
                title="Open landing page"
                className="inline-flex items-center justify-center w-8 h-8 rounded-md border border-gray-200 text-gray-500 hover:border-[#0d64a8] hover:text-[#0d64a8]"
              >
                <ExternalLink size={13} />
              </a>
            </div>
          </div>
        ))}
      </div>

      <p className="text-[11px] text-gray-400 mt-3 flex items-center gap-1">
        <Zap size={11} /> Conversion % = enquiries ÷ tracked visits. Values may exceed 100% for pages with enquiries logged before view-tracking was live.
      </p>
    </div>
  );
};

export default CampaignAnalytics;
